




































































































































import { mixins } from "vue-class-component";
import { Component, Inject, Prop, Watch } from "vue-property-decorator";
import DatePicker from "vuejs-datepicker";
import Multiselect from "vue-multiselect";
import { create } from "vue-modal-dialogs";
import { getModule } from "vuex-module-decorators";

import FormErrors from "@/lib/components/FormErrors.vue";
import FormField from "@/lib/components/FormField.vue";
import LoadingSpinner from "@/lib/components/LoadingSpinner.vue";
import SubmitButton from "@/lib/components/SubmitButton.vue";

import GeneralFormMixin from "@/lib/mixins/GeneralForm";
import IdGenerator from "@/lib/services/id-generator";
import dataStore from "@/store";
import Product from "@/modules/plushie/product.model";
import PlushieStore from "@/modules/plushie/store";

import HolidayPeriodProductGroupDialog from "../ProductGroup/Dialog.vue";

import HolidayPeriod from "../../holiday-period.model";
import {
  HolidayPeriodGroupDraft,
  buildProductSettings,
  combineMatchingGroups,
  copyProductSettingsToDraft,
  groupProductSettings,
  validateGroupDraft,
} from "../../holiday-period-group-configuration.helper";
import ProductSetting from "../../product-setting.model";
import HolidayPeriodStore from "../../store";

interface GroupDialogProps {
  editing?: boolean;
  productIds?: string[];
  unavailableProductIds?: string[];
}

@Component({
  components: {
    DatePicker,
    FormErrors,
    FormField,
    LoadingSpinner,
    Multiselect,
    SubmitButton,
  },
})
export default class HolidayPeriodProductGroupSettingsEditForm extends mixins(
  GeneralFormMixin
) {
  @Prop({ required: true })
  public readonly periodId!: string;

  @Inject("IdGenerator")
  private fIdGenerator!: IdGenerator;

  public sourcePeriod: HolidayPeriod | null = null;

  private fHolidayPeriodStore: HolidayPeriodStore;
  private fPlushieStore: PlushieStore;
  private fIsLoading = true;
  private fLoadFailed = false;
  private fCopyMessage = "";
  private fCopySucceeded = false;
  private fIsCopying = false;
  private fCopyFeedbackResetTimeoutId?: number;
  private fDraftGroups: HolidayPeriodGroupDraft[] = [];
  private fExistingSettings: ProductSetting[] = [];

  get isLoading(): boolean {
    return this.fIsLoading;
  }

  get loadFailed(): boolean {
    return this.fLoadFailed;
  }

  get copyMessage(): string {
    return this.fCopyMessage;
  }

  get copySucceeded(): boolean {
    return this.fCopySucceeded;
  }

  get isCopying(): boolean {
    return this.fIsCopying;
  }

  get isDisabled(): boolean {
    return this.fIsDisabled || this.disable || this.fIsCopying;
  }

  get draftGroups(): HolidayPeriodGroupDraft[] {
    return this.fDraftGroups;
  }

  get sourcePeriodOptions(): HolidayPeriod[] {
    return this.fHolidayPeriodStore.holidayPeriodsList.filter(
      (period) => period.id !== this.periodId
    );
  }

  constructor() {
    super();
    this.fHolidayPeriodStore = getModule(HolidayPeriodStore, dataStore);
    this.fPlushieStore = getModule(PlushieStore, dataStore);
  }

  public getProductNames(group: HolidayPeriodGroupDraft): string {
    return group.productIds
      .map((id) => this.fPlushieStore.getProductById(id))
      .filter((product): product is Product => product !== undefined)
      .map((product) => product.name)
      .sort((left, right) =>
        left.toLowerCase().localeCompare(right.toLowerCase())
      )
      .join(", ");
  }

  public async addGroup(): Promise<void> {
    if (this.isDisabled) {
      return;
    }
    const dialog = create<GroupDialogProps, string[] | undefined>(
      HolidayPeriodProductGroupDialog
    );
    const productIds = await dialog({
      unavailableProductIds: this.getAssignedProductIds(),
    });
    if (!productIds) {
      return;
    }
    if (
      !productIds.length ||
      productIds.some((id) => this.getAssignedProductIds().includes(id))
    ) {
      this.submitErrors = [
        "Select at least one unassigned product for the group.",
      ];
      return;
    }
    this.fDraftGroups.push({ key: this.fIdGenerator.getId(), productIds });
    this.fCopyMessage = "";
    this.submitErrors = [];
  }

  public async editGroup(group: HolidayPeriodGroupDraft): Promise<void> {
    if (this.isDisabled) {
      return;
    }
    const dialog = create<GroupDialogProps, string[] | undefined>(
      HolidayPeriodProductGroupDialog
    );
    const productIds = await dialog({
      editing: true,
      productIds: group.productIds,
      unavailableProductIds: this.getAssignedProductIds(group.key),
    });
    if (!productIds) {
      return;
    }
    if (
      !productIds.length ||
      productIds.some((id) =>
        this.getAssignedProductIds(group.key).includes(id)
      )
    ) {
      this.submitErrors = [
        "Select at least one unassigned product for the group.",
      ];
      return;
    }
    const index = this.fDraftGroups.findIndex((item) => item.key === group.key);
    if (index !== -1) {
      this.fDraftGroups.splice(index, 1, { ...group, productIds });
      this.submitErrors = [];
    }
  }

  public removeGroup(group: HolidayPeriodGroupDraft): void {
    if (this.isDisabled) {
      return;
    }
    this.fDraftGroups = this.fDraftGroups.filter(
      (item) => item.key !== group.key
    );
  }

  public async copySourceConfiguration(): Promise<void> {
    if (!this.sourcePeriod || this.isDisabled || this.fIsCopying) {
      return;
    }
    const sourcePeriodId = this.sourcePeriod.id;
    this.resetCopyFeedback();
    this.fCopyMessage = "";
    this.fIsCopying = true;
    try {
      const settings = await this.fHolidayPeriodStore.loadProductSettingsByPeriodId(
        {
          periodId: sourcePeriodId,
          useCache: true,
        }
      );
      if (this.sourcePeriod?.id !== sourcePeriodId) {
        return;
      }
      if (!settings.length) {
        this.fCopyMessage = "The selected period has no settings to copy.";
        return;
      }
      this.fDraftGroups = copyProductSettingsToDraft(
        settings,
        () => this.fIdGenerator.getId(),
        new Date().getFullYear()
      );
      this.submitErrors = [];
      this.fCopySucceeded = true;
      this.fCopyFeedbackResetTimeoutId = window.setTimeout(
        () => this.resetCopyFeedback(),
        1800
      );
    } catch (error) {
      this.submitErrors = this.fErrorConverterService.describeError(error);
    } finally {
      this.fIsCopying = false;
    }
  }

  public onCancelClick(): void {
    if (!this.isDisabled) {
      this.$emit("cancel");
    }
  }

  protected async created(): Promise<void> {
    this.$once("hook:beforeDestroy", () => this.resetCopyFeedback());
    this.fIsLoading = true;
    try {
      const [settings] = await Promise.all([
        this.fHolidayPeriodStore.loadProductSettingsByPeriodId({
          periodId: this.periodId,
          useCache: true,
        }),
        this.fHolidayPeriodStore.loadHolidayPeriods(),
        this.fPlushieStore.loadProducts(),
      ]);
      this.fExistingSettings = settings;
      this.fDraftGroups = groupProductSettings(settings, () =>
        this.fIdGenerator.getId()
      );
    } catch (error) {
      this.fLoadFailed = true;
      this.submitErrors = this.fErrorConverterService.describeError(error);
    } finally {
      this.fIsLoading = false;
    }
  }

  protected async performSubmit(): Promise<void> {
    if (this.fIsCopying) {
      return;
    }

    if (this.fLoadFailed) {
      throw new Error("Holiday product settings could not be loaded.");
    }

    const errors = validateGroupDraft(this.fDraftGroups);
    if (errors.length) {
      this.submitErrors = errors;
      return;
    }

    const values = buildProductSettings(
      this.fDraftGroups,
      this.periodId,
      this.fExistingSettings,
      () => this.fIdGenerator.getId()
    );
    const saved = await this.fHolidayPeriodStore.saveProductSettingsForPeriod({
      periodId: this.periodId,
      values,
    });
    this.fExistingSettings = saved;
    this.fDraftGroups = combineMatchingGroups(
      groupProductSettings(saved, () => this.fIdGenerator.getId())
    );
    this.$emit("updated");
  }

  private getAssignedProductIds(exceptKey?: string): string[] {
    return this.fDraftGroups
      .filter((group) => group.key !== exceptKey)
      .reduce((ids: string[], group) => ids.concat(group.productIds), []);
  }

  private resetCopyFeedback(): void {
    if (this.fCopyFeedbackResetTimeoutId !== undefined) {
      clearTimeout(this.fCopyFeedbackResetTimeoutId);
      this.fCopyFeedbackResetTimeoutId = undefined;
    }
    this.fCopySucceeded = false;
  }

  @Watch("sourcePeriod")
  private onSourcePeriodChanged(): void {
    this.resetCopyFeedback();
  }
}
